// ============================================
// pages/gunny-bags/GunnyBagsDashboard.tsx — Real dashboard (client-side rollup)
// Reads live from gunny_suppliers, gunny_pos, gunny_utilization.
// ============================================
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Package, Loader2, Users, FileText, PackageCheck,
  ArrowUpRight, AlertTriangle,
} from 'lucide-react';
import {
  seasonsAPI, gunnySuppliersAPI, gunnyPosAPI, gunnyUtilizationAPI,
} from '../../api/services';
import { formatAmount, num } from '../../types/markfed';
import type { Season } from '../../types/markfed';

export const GunnyBagsDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState<number>(0);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [pos, setPos] = useState<any[]>([]);
  const [utilization, setUtilization] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, sup] = await Promise.all([seasonsAPI.list(), gunnySuppliersAPI.list()]);
        const list: Season[] = Array.isArray(s.data) ? s.data : [];
        setSeasons(list);
        setSuppliers(sup.data);
        const active = list.find((x) => x.is_active) || list[0];
        if (active) setSelectedSeasonId(active.id);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to load');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedSeasonId) return;
    (async () => {
      setLoading(true);
      try {
        const [p, u] = await Promise.all([
          gunnyPosAPI.list(selectedSeasonId),
          gunnyUtilizationAPI.list(selectedSeasonId),
        ]);
        setPos(Array.isArray(p.data) ? p.data : []);
        setUtilization(Array.isArray(u.data) ? u.data : []);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to load season data');
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedSeasonId]);

  const totals = useMemo(() => {
    const poTotal = pos.reduce((s, p) => s + num(p.total_amount), 0);
    const poBags = pos.reduce((s, p) => s + num(p.total_bags), 0);
    const supplied = utilization.reduce((s, u) => s + num(u.bags_supplied), 0);
    const utilized = utilization.reduce((s, u) => s + num(u.bags_utilized), 0);
    const returned = utilization.reduce((s, u) => s + num(u.bags_returned), 0);
    const balance = Math.max(supplied - utilized - returned, 0);
    const grossPayable = utilization.reduce((s, u) => s + num(u.gross_amount), 0);
    const released = utilization.reduce((s, u) => s + num(u.payment_released), 0);
    const pendingPayment = Math.max(grossPayable - released, 0);
    return { poTotal, poBags, supplied, utilized, returned, balance, grossPayable, released, pendingPayment };
  }, [pos, utilization]);

  const pendingPOs = useMemo(
    () => pos.filter((p) => ['submitted', 'dm_verified'].includes(p.status || '')).length,
    [pos],
  );

  if (loading && utilization.length === 0) {
    return (
      <div className="max-w-7xl mx-auto flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-amber-600 animate-spin" />
        <span className="ml-2 text-gray-500">Loading...</span>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <Package className="w-7 h-7 text-amber-600" />
          Gunny Bags Dashboard
        </h1>
        <p className="text-sm text-gray-500 mt-1">Live rollup of suppliers, purchase orders, and utilization</p>
      </div>

      <div className="mb-4 max-w-xs">
        <label className="block text-xs font-medium text-gray-600 mb-1">Season</label>
        <select value={selectedSeasonId} onChange={(e) => setSelectedSeasonId(parseInt(e.target.value))}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
          {seasons.map((s) => (<option key={s.id} value={s.id}>{s.season_name}</option>))}
        </select>
      </div>

      {error && <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{error}</div>}

      {/* Top stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div onClick={() => navigate('/admin/gunny-suppliers')}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 cursor-pointer hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-2">
            <Users className="w-6 h-6 text-blue-600" />
            <ArrowUpRight className="w-4 h-4 text-gray-300" />
          </div>
          <p className="text-xs text-gray-500">Suppliers</p>
          <p className="text-3xl font-bold text-gray-900">{suppliers.length}</p>
        </div>
        <div onClick={() => navigate('/gunny-bags/procurement')}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 cursor-pointer hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-2">
            <FileText className="w-6 h-6 text-amber-600" />
            <ArrowUpRight className="w-4 h-4 text-gray-300" />
          </div>
          <p className="text-xs text-gray-500">Purchase Orders</p>
          <p className="text-3xl font-bold text-gray-900">{pos.length}</p>
          <p className="text-[10px] text-gray-400 mt-1">{formatAmount(totals.poTotal)} value</p>
        </div>
        <div onClick={() => navigate('/gunny-bags/utilization')}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 cursor-pointer hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-2">
            <PackageCheck className="w-6 h-6 text-green-600" />
            <ArrowUpRight className="w-4 h-4 text-gray-300" />
          </div>
          <p className="text-xs text-gray-500">Utilization Records</p>
          <p className="text-3xl font-bold text-gray-900">{utilization.length}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-2">
            <AlertTriangle className={`w-6 h-6 ${pendingPOs > 0 ? 'text-orange-600' : 'text-gray-400'}`} />
          </div>
          <p className="text-xs text-gray-500">Pending PO Approvals</p>
          <p className="text-3xl font-bold text-gray-900">{pendingPOs}</p>
          <p className="text-[10px] text-gray-400 mt-1">awaiting DM/MD action</p>
        </div>
      </div>

      {/* Bag flow summary */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <h3 className="text-sm font-bold text-gray-800 mb-4">Bag Flow</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-xs text-gray-600">Bags Supplied</p>
            <p className="text-2xl font-bold text-blue-700">{totals.supplied.toLocaleString('en-IN')}</p>
          </div>
          <div className="p-4 bg-green-50 rounded-lg border border-green-200">
            <p className="text-xs text-gray-600">Bags Utilized</p>
            <p className="text-2xl font-bold text-green-700">{totals.utilized.toLocaleString('en-IN')}</p>
            <p className="text-[10px] text-gray-400">{totals.supplied > 0 ? ((totals.utilized / totals.supplied) * 100).toFixed(1) : 0}% of supplied</p>
          </div>
          <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
            <p className="text-xs text-gray-600">Bags Returned</p>
            <p className="text-2xl font-bold text-yellow-700">{totals.returned.toLocaleString('en-IN')}</p>
          </div>
          <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
            <p className="text-xs text-gray-600">Stock on Hand</p>
            <p className="text-2xl font-bold text-purple-700">{totals.balance.toLocaleString('en-IN')}</p>
            <p className="text-[10px] text-gray-400">supplied − used − returned</p>
          </div>
        </div>
      </div>

      {/* Payment summary */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <h3 className="text-sm font-bold text-gray-800 mb-4">Payment Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-600">Gross Payable</p>
            <p className="text-xl font-bold text-gray-900">{formatAmount(totals.grossPayable)}</p>
          </div>
          <div className="p-4 bg-green-50 rounded-lg">
            <p className="text-xs text-gray-600">Released</p>
            <p className="text-xl font-bold text-green-700">{formatAmount(totals.released)}</p>
            <p className="text-[10px] text-gray-400">{totals.grossPayable > 0 ? ((totals.released / totals.grossPayable) * 100).toFixed(1) : 0}%</p>
          </div>
          <div className="p-4 bg-orange-50 rounded-lg">
            <p className="text-xs text-gray-600">Pending</p>
            <p className="text-xl font-bold text-orange-700">{formatAmount(totals.pendingPayment)}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <button onClick={() => navigate('/gunny-bags/inventory')}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 text-left hover:shadow-md hover:border-amber-300 transition-all">
          <h4 className="text-sm font-bold text-gray-800">Inventory Report</h4>
          <p className="text-xs text-gray-500 mt-1">Stock on hand by supplier × district × society</p>
        </button>
        <button onClick={() => navigate('/gunny-bags/performance')}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 text-left hover:shadow-md hover:border-amber-300 transition-all">
          <h4 className="text-sm font-bold text-gray-800">Supplier Performance</h4>
          <p className="text-xs text-gray-500 mt-1">On-time delivery, return rate, payment settlement</p>
        </button>
      </div>
    </div>
  );
};

export default GunnyBagsDashboard;
